import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getDashboard, type Dashboard } from '../dashboards.api';
import ChartCard, { ChartCardSkeleton } from '../components/ChartCard';
import DataTableCard, { DataTableCardSkeleton } from '../components/DataTableCard';
import KpiTile, { KpiTileSkeleton } from '../components/KpiTile';

type Failure = { kind: 'forbidden' | 'missing' | 'error'; message: string };

const KPI_GRID = { display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' } as const;
const CARD_GRID = { display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))' } as const;

/** `/dashboards/:module` — one module's headline numbers, charts and short lists. */
export default function ModuleDashboardPage() {
  const { module = '' } = useParams();
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<Failure | null>(null);
  const request = useRef(0);

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setFailure(null);
    try {
      const d = await getDashboard(module);
      if (id === request.current) setData(d);
    } catch (err: any) {
      if (id !== request.current) return;
      const status = err?.response?.status;
      if (status === 403) setFailure({ kind: 'forbidden', message: 'You do not have access to this module.' });
      else if (status === 404) setFailure({ kind: 'missing', message: 'There is no dashboard for this module.' });
      else setFailure({ kind: 'error', message: err?.userMessage || 'Failed to load the dashboard.' });
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [module]);

  // A different module starts from a blank frame; a refresh keeps the current one.
  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  if (failure && failure.kind !== 'error') {
    return (
      <Stack alignItems='center' justifyContent='center' gap={2} sx={{ py: 10, textAlign: 'center', color: 'text.secondary' }}>
        <Icon icon={failure.kind === 'forbidden' ? 'mdi:lock-outline' : 'mdi:view-dashboard-variant-outline'} width={40} />
        <Typography variant='subtitle1' color='text.primary'>
          {failure.kind === 'forbidden' ? 'You do not have access to this module' : 'Dashboard not found'}
        </Typography>
        <Typography variant='body2'>
          {failure.kind === 'forbidden'
            ? 'Your role cannot open any screen of this module. Ask the admin if you need it.'
            : failure.message}
        </Typography>
        <Button component={RouterLink} to='/' variant='outlined'>
          Back to Home
        </Button>
      </Stack>
    );
  }

  const firstLoad = loading && !data;
  const updated = data?.generatedAt ? dayjs(data.generatedAt) : null;

  return (
    <Stack gap={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'flex-end' }} gap={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {firstLoad ? (
            <>
              <Skeleton variant='text' width={220} height={36} />
              <Skeleton variant='text' width={320} />
            </>
          ) : (
            <>
              <Typography component='h1' sx={{ fontSize: '1.375rem', fontWeight: 600 }}>
                {data?.title ?? 'Dashboard'}
              </Typography>
              {data?.subtitle && (
                <Typography variant='body2' color='text.secondary'>
                  {data.subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Stack direction='row' alignItems='center' gap={1.5}>
          {updated?.isValid() && (
            <Typography variant='caption' color='text.secondary' title={updated.format('DD MMM YYYY, hh:mm:ss A')}>
              Updated {updated.format('DD MMM, hh:mm A')}
            </Typography>
          )}
          <Button
            variant='outlined'
            size='small'
            onClick={load}
            disabled={loading}
            startIcon={<Icon icon='mdi:refresh' width={18} />}
          >
            {loading && data ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {failure && (
        <Alert severity='error' action={<Button onClick={load}>Retry</Button>}>
          {failure.message}
        </Alert>
      )}

      {firstLoad ? (
        <>
          <Box sx={KPI_GRID}>
            {[0, 1, 2, 3].map((i) => (
              <KpiTileSkeleton key={i} />
            ))}
          </Box>
          <Box sx={CARD_GRID}>
            <ChartCardSkeleton />
            <ChartCardSkeleton />
          </Box>
          <Box sx={CARD_GRID}>
            <DataTableCardSkeleton />
          </Box>
        </>
      ) : (
        data && (
          // While refreshing, the current figures stay in place, dimmed, so nothing jumps.
          <Stack gap={3} sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s' }} aria-busy={loading}>
            {data.kpis?.length > 0 && (
              <Box sx={KPI_GRID}>
                {data.kpis.map((k) => (
                  <KpiTile key={k.key} kpi={k} />
                ))}
              </Box>
            )}
            {data.charts?.length > 0 && (
              <Box sx={CARD_GRID}>
                {data.charts.map((c) => (
                  <ChartCard key={c.key} chart={c} />
                ))}
              </Box>
            )}
            {data.tables?.length > 0 && (
              <Box sx={CARD_GRID}>
                {data.tables.map((t) => (
                  <DataTableCard key={t.key} table={t} />
                ))}
              </Box>
            )}
            {!data.kpis?.length && !data.charts?.length && !data.tables?.length && (
              <Typography variant='body2' color='text.secondary' sx={{ py: 6, textAlign: 'center' }}>
                Nothing to show on this dashboard yet.
              </Typography>
            )}
          </Stack>
        )
      )}
    </Stack>
  );
}
